import type { ForwardedRef, ChangeEvent, MouseEvent, FC } from 'react'
import { forwardRef, useRef } from 'react'
import { InputAdornment } from '@mui/material'
import { IconButton, TextField, type TextFieldProps } from '@dhl-ui/components'
import { CloseIcon, SearchIcon } from '@dhl-ui/icons'

type Props = {
  onSearchClear?: () => void
  onSearchChange?: (searchTerm: string) => void
  searchTerm: string | null | undefined
  showSearchIcon?: boolean
  sx?: TextFieldProps['sx']
}

export const SearchBox: FC<Props> = forwardRef(
  (
    { sx, onSearchClear, onSearchChange, searchTerm, showSearchIcon }: Props,
    ref: ForwardedRef<HTMLDivElement> | undefined
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => onSearchChange?.(event.target.value)

    const handleSearchClear = () => {
      onSearchClear?.()
      inputRef?.current?.focus()
    }

    const handleClearMouseDown = (event: MouseEvent) => event.preventDefault()

    return (
      <TextField
        autoFocus
        data-test="search-input-field"
        inputRef={inputRef}
        ref={ref}
        size="small"
        value={searchTerm}
        onChange={handleSearchChange}
        slotProps={{
          input: {
            startAdornment: showSearchIcon && (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment className="search-clear-button" data-test="search-input-field-clear" position="end">
                <IconButton
                  aria-label="clear"
                  edge="end"
                  onClick={handleSearchClear}
                  onMouseDown={handleClearMouseDown}
                  size="xs"
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={sx}
      />
    )
  }
)
